/**
 * RealtimeVoiceSession Component
 * ================================
 * WebRTC-based real-time speech-to-speech using OpenAI Realtime API.
 * 
 * Features:
 * - Low-latency bidirectional voice
 * - Barge-in support (interrupt AI by speaking)
 * - Session controls (connect/disconnect/mute)
 * - Automatic reconnection handling
 * - Text sanitization (strips <memory>, <action> tags before display)
 * 
 * Architecture:
 * Browser → WebRTC → OpenAI Realtime API (via backend session token)
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  WarningCircle,
  Lightning,
  CircleNotch,
  Microphone,
  MicrophoneSlash,
  Phone,
  PhoneDisconnect,
  SpeakerX,
  SpeakerHigh,
  WifiHigh,
  WifiSlash
} from '@phosphor-icons/react';
// Connection states
const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

// ═══════════════════════════════════════════════════════════════════════════
// SANITIZE ASSISTANT OUTPUT: Strip XML-like tags before display/TTS
// ═══════════════════════════════════════════════════════════════════════════
const sanitizeAssistantOutput = (text) => {
  if (!text) return '';
  
  let sanitized = text;
  
  // Remove <memory>...</memory> blocks (including multiline)
  sanitized = sanitized.replace(/<memory>[\s\S]*?<\/memory>/gi, '');
  
  // Remove <action>...</action> blocks (including multiline)
  sanitized = sanitized.replace(/<action>[\s\S]*?<\/action>/gi, '');
  
  // Remove any orphaned tags
  sanitized = sanitized.replace(/<\/?memory>/gi, '');
  sanitized = sanitized.replace(/<\/?action>/gi, '');
  
  // Clean up excessive whitespace
  sanitized = sanitized.replace(/\n{3,}/g, '\n\n').trim();
  
  // If sanitization removed everything, use fallback message
  if (!sanitized || sanitized.length < 3) {
    return "I'm processing that for you now.";
  }
  
  return sanitized;
};

const RealtimeVoiceSession = ({
  agentId,
  agentName,
  businessName,
  businessDescription,
  customKnowledge,  // Text-based knowledge content
  requiredBookingInfo,
  voice = 'alloy',
  welcomeMessage,
  pauseTolerance = 1000,
  threadId,
  onMessage,
  onError,
  onStateChange
}) => {
  const { activeOrganization } = useOrganization();
  
  // Connection state
  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
  const [sessionId, setSessionId] = useState(null);
  const [keySource, setKeySource] = useState(null);
  
  // Audio state
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isOutputMuted, setIsOutputMuted] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  
  // Session stats
  const [sessionDuration, setSessionDuration] = useState(0);
  const [error, setError] = useState(null);
  
  // Refs
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const audioElementRef = useRef(null);
  const localStreamRef = useRef(null);
  const sessionTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STATE CHANGE HANDLER
  // ═══════════════════════════════════════════════════════════════════════════
  
  const updateConnectionState = useCallback((newState) => {
    setConnectionState(newState);
    onStateChange?.(newState);
    console.log(`[RealtimeVoice] State: ${newState}`);
  }, [onStateChange]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // BARGE-IN DETECTION
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleVoiceActivityStart = useCallback(() => {
    setIsUserSpeaking(true);
    
    // If AI is speaking and user starts talking, this is a barge-in
    if (isAISpeaking) {
      console.log('[RealtimeVoice] Barge-in detected - user interrupted AI');
      
      // Send cancel response event via data channel
      if (dataChannelRef.current?.readyState === 'open') {
        try {
          dataChannelRef.current.send(JSON.stringify({
            type: 'response.cancel'
          }));
          console.log('[RealtimeVoice] Sent response.cancel for barge-in');
        } catch (e) {
          console.error('[RealtimeVoice] Failed to send cancel:', e);
        }
      }
      
      setIsAISpeaking(false);
    }
  }, [isAISpeaking]);
  
  const handleVoiceActivityEnd = useCallback(() => {
    setIsUserSpeaking(false);
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // WEBRTC SETUP
  // ═══════════════════════════════════════════════════════════════════════════
  
  const setupAudioElement = useCallback(() => {
    if (!audioElementRef.current) {
      const audio = document.createElement('audio');
      audio.autoplay = true;
      audio.id = 'realtime-audio-output';
      document.body.appendChild(audio);
      audioElementRef.current = audio;
    }
    
    // Handle remote audio track
    if (peerConnectionRef.current) {
      peerConnectionRef.current.ontrack = (event) => {
        console.log('[RealtimeVoice] Received remote audio track');
        if (audioElementRef.current) {
          audioElementRef.current.srcObject = event.streams[0];
          audioElementRef.current.muted = isOutputMuted;
        }
      };
    }
  }, [isOutputMuted]);
  
  const setupLocalAudio = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      localStreamRef.current = stream;
      
      // Add tracks to peer connection
      stream.getTracks().forEach(track => {
        peerConnectionRef.current?.addTrack(track, stream);
        
        // Set up voice activity detection
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        
        analyser.fftSize = 256;
        microphone.connect(analyser);
        
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        let speaking = false;
        const threshold = 30; // Adjust based on testing
        
        const checkVoiceActivity = () => {
          if (!localStreamRef.current) return;
          
          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          
          if (average > threshold && !speaking) {
            speaking = true;
            handleVoiceActivityStart();
          } else if (average <= threshold && speaking) {
            speaking = false;
            handleVoiceActivityEnd();
          }
          
          if (connectionState === ConnectionState.CONNECTED) {
            requestAnimationFrame(checkVoiceActivity);
          }
        };
        
        checkVoiceActivity();
      });
      
      console.log('[RealtimeVoice] Local audio setup complete');
    } catch (err) {
      console.error('[RealtimeVoice] Failed to get microphone:', err);
      throw new Error('Microphone access denied. Please allow microphone access.');
    }
  }, [connectionState, handleVoiceActivityStart, handleVoiceActivityEnd]);
  
  const setupDataChannel = useCallback(() => {
    if (!peerConnectionRef.current) return;
    
    const dataChannel = peerConnectionRef.current.createDataChannel('oai-events');
    dataChannelRef.current = dataChannel;
    
    dataChannel.onopen = () => {
      console.log('[RealtimeVoice] Data channel opened');
      
      // Build comprehensive agent instructions including business context
      let agentInstructions = `You are a helpful AI voice assistant for ${businessName || 'this business'}. Your role is to assist callers professionally, answer questions about the business, and help with booking appointments.`;
      
      // Add business description context if available
      if (businessDescription) {
        agentInstructions += `\n\nAbout the business:\n${businessDescription}`;
      }
      
      // Add custom knowledge content if available
      if (customKnowledge && customKnowledge.trim()) {
        agentInstructions += `\n\n## Knowledge Base\nUse the following information to answer questions:\n\n${customKnowledge}`;
      }
      
      // Add required booking information if configured
      if (requiredBookingInfo && requiredBookingInfo.length > 0) {
        const infoLabels = {
          'name': 'Full Name',
          'phone': 'Phone Number',
          'email': 'Email Address',
          'address': 'Address',
          'notes': 'Special Notes/Requests',
          'payment': 'Payment Method'
        };
        const requiredFields = requiredBookingInfo.map(id => infoLabels[id] || id).join(', ');
        agentInstructions += `\n\nWhen making a booking, you MUST collect the following information from the caller: ${requiredFields}. Do not finalize a booking until all required information has been provided.`;
      }
      
      agentInstructions += `\n\nBe warm, professional, and concise. Speak clearly. Confirm important details by repeating them back.`;
      
      // Send session configuration - NO session.modalities (causes "Unknown parameter" error)
      // Only use turn_detection, voice, instructions at session level
      const sessionConfig = {
        type: 'session.update',
        session: {
          voice: voice,
          instructions: agentInstructions,
          input_audio_transcription: { model: 'whisper-1' },
          turn_detection: {
            type: 'server_vad',
            threshold: 0.55,            // Less sensitive (0.45-0.6 range)
            prefix_padding_ms: 500,     // Keep more context before speech
            silence_duration_ms: 1400,  // More tolerant of pauses
            idle_timeout_ms: pauseTolerance  // Configurable (default 2000ms)
          }
        }
      };
      
      console.log('[RealtimeVoice] Sending session.update:', JSON.stringify(sessionConfig, null, 2));
      dataChannel.send(JSON.stringify(sessionConfig));
      
      // Prompt AI to speak the welcome greeting IMMEDIATELY
      setTimeout(() => {
        if (dataChannel.readyState === 'open') {
          const greetingText = welcomeMessage || `Hello! Thank you for calling ${businessName || 'our business'}. How can I help you today?`;
          
          const responseConfig = {
            type: 'response.create',
            response: {
              output_modalities: ['audio'],  // Audio only (text via transcripts)
              instructions: `Say this greeting exactly, then ask how you can help:\n\n"${greetingText}"`
            }
          };
          
          console.log('[RealtimeVoice] Sending response.create:', JSON.stringify(responseConfig, null, 2));
          dataChannel.send(JSON.stringify(responseConfig));
        }
      }, 300); // Small delay to let session.update be processed
    };
    
    dataChannel.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[RealtimeVoice] Event:', data.type);
        
        switch (data.type) {
          // AI speaking events - GATE THE MIC
          case 'output_audio_buffer.started':
            setIsAISpeaking(true);
            // Disable mic track while AI is speaking (prevents echo/self-interruption)
            if (localStreamRef.current) {
              localStreamRef.current.getAudioTracks().forEach(track => {
                track.enabled = false;
                console.log('[RealtimeVoice] Microphone DISABLED - AI speaking');
              });
            }
            break;
            
          case 'response.audio.delta':
          case 'response.audio_transcript.delta':
            setIsAISpeaking(true);
            break;
            
          case 'output_audio_buffer.stopped':
            setIsAISpeaking(false);
            // Re-enable mic track after AI finishes speaking WITH HANDOVER DELAY
            // This prevents immediate re-trigger from trailing audio
            if (localStreamRef.current && !isMicMuted) {
              setTimeout(() => {
                if (localStreamRef.current) {
                  localStreamRef.current.getAudioTracks().forEach(track => {
                    track.enabled = true;
                    console.log('[RealtimeVoice] Microphone ENABLED - AI finished (after 300ms handover)');
                  });
                }
              }, 300); // 300ms handover delay
            }
            break;
            
          case 'response.audio.done':
          case 'response.done':
            setIsAISpeaking(false);
            break;
            
          // User speech events
          case 'input_audio_buffer.speech_started':
            handleVoiceActivityStart();
            break;
            
          case 'input_audio_buffer.speech_stopped':
            handleVoiceActivityEnd();
            break;
            
          case 'conversation.item.created':
            // Pass transcripts to parent (sanitize assistant output to remove <memory>/<action> tags)
            if (data.item?.role === 'assistant' && data.item?.content) {
              const rawText = data.item.content
                .filter(c => c.type === 'text' || c.type === 'audio')
                .map(c => c.text || c.transcript)
                .join(' ');
              // Apply sanitization to strip XML-like tags before displaying
              const sanitizedText = sanitizeAssistantOutput(rawText);
              if (sanitizedText) {
                onMessage?.({ role: 'assistant', content: sanitizedText });
              }
            } else if (data.item?.role === 'user' && data.item?.content) {
              const text = data.item.content
                .filter(c => c.type === 'input_audio')
                .map(c => c.transcript)
                .join(' ');
              if (text) {
                onMessage?.({ role: 'user', content: text });
              }
            }
            break;
            
          case 'error':
            const errorMsg = data.error?.message || 'Unknown error';
            console.error('[RealtimeVoice] API Error:', data.error);
            
            // Filter out non-critical parameter warnings that don't affect functionality
            // These can occur during session setup but the call still works
            const isNonCritical = errorMsg.includes('Unknown parameter') || 
                                  errorMsg.includes('Missing required parameter');
            
            if (isNonCritical) {
              console.warn('[RealtimeVoice] Non-critical API warning (ignoring):', errorMsg);
              // Don't show these to the user - they're parameter mismatches during setup
            } else {
              setError(errorMsg);
              onError?.(data.error);
            }
            break;
        }
      } catch (e) {
        console.error('[RealtimeVoice] Failed to parse event:', e);
      }
    };
    
    dataChannel.onerror = (err) => {
      console.error('[RealtimeVoice] Data channel error:', err);
    };
    
    dataChannel.onclose = (event) => {
      console.warn('[RealtimeVoice] Data channel closed. Event:', event);
      console.warn('[RealtimeVoice] Data channel close - connection state was:', connectionState);
    };
  }, [voice, welcomeMessage, pauseTolerance, businessName, businessDescription, customKnowledge, requiredBookingInfo, onMessage, onError, handleVoiceActivityStart, handleVoiceActivityEnd, connectionState, isMicMuted]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CONNECTION MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════
  
  const connect = useCallback(async () => {
    if (connectionState === ConnectionState.CONNECTING || connectionState === ConnectionState.CONNECTED) {
      console.log('[RealtimeVoice] Already connecting/connected');
      return;
    }
    
    updateConnectionState(ConnectionState.CONNECTING);
    setError(null);
    
    try {
      // Step 1: Create WebRTC peer connection FIRST
      console.log('[RealtimeVoice] Creating WebRTC peer connection...');
      peerConnectionRef.current = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      
      // Set up audio handling
      setupAudioElement();
      await setupLocalAudio();
      setupDataChannel();
      
      // Step 2: Create and set local description (offer)
      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);
      
      // ═══════════════════════════════════════════════════════════════════════════
      // SDP INTEGRITY LOGGING - FRONTEND
      // ═══════════════════════════════════════════════════════════════════════════
      const offerSdp = offer.sdp;
      console.log('═══════════════════════════════════════════════════════════');
      console.log('[FE SDP] UNIFIED INTERFACE - No ephemeral token needed');
      console.log('[FE SDP] Offer length:', offerSdp.length);
      console.log('[FE SDP] First 60 chars:', offerSdp.substring(0, 60));
      console.log('[FE SDP] Last 60 chars:', offerSdp.substring(offerSdp.length - 60));
      console.log('═══════════════════════════════════════════════════════════');
      
      // Step 3: Send offer to backend (which combines with session config and sends to OpenAI)
      const agentInstructions = welcomeMessage 
        ? `You are a helpful voice assistant for ${businessName || 'this business'}. Your role is to assist callers professionally.`
        : `You are a helpful voice assistant for ${businessName || 'this business'}. Be concise and helpful.`;
      
      const requestBody = JSON.stringify({
        sdp_offer: offerSdp,
        model: 'gpt-4o-realtime-preview-2024-12-17',
        voice: voice,
        instructions: agentInstructions
      });
      console.log('[FE SDP] Request body length:', requestBody.length);
      
      const negotiateResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/voice/realtime/negotiate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: requestBody
        },
        activeOrganization?.organization_id
      );
      
      if (!negotiateResponse.ok) {
        let errorMessage = 'WebRTC negotiation failed';
        try {
          const errorData = await negotiateResponse.json();
          console.error('[RealtimeVoice] Negotiation error:', errorData);
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          const errorText = await negotiateResponse.text();
          console.error('[RealtimeVoice] Negotiation error (text):', errorText);
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      // Parse response - should be JSON with {sdp: "v=0..."}
      const responseData = await negotiateResponse.json();
      console.log('[RealtimeVoice] Negotiate response keys:', Object.keys(responseData));
      
      const rawSdp = responseData.sdp || responseData.sdp_answer;
      
      // Validate SDP answer format
      if (!rawSdp) {
        console.error('[RealtimeVoice] No SDP in response:', responseData);
        throw new Error('No SDP answer received from backend');
      }
      
      console.log('[RealtimeVoice] SDP answer length:', rawSdp.length);
      console.log('[RealtimeVoice] SDP answer first 60 chars:', rawSdp.substring(0, 60));
      
      // ═══════════════════════════════════════════════════════════════════════════
      // NORMALIZE SDP: Fix newline issues before setRemoteDescription
      // WebRTC expects strict CRLF line endings
      // ═══════════════════════════════════════════════════════════════════════════
      const normalizeSdp = (s) => {
        if (!s) return s;
        
        // If backend accidentally sends literal backslashes, convert to real newlines
        s = s.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n").replace(/\\r/g, "\n");
        
        // Normalize all newline types to CRLF as WebRTC expects
        s = s.replace(/\r?\n/g, "\r\n").trim();
        
        // Ensure SDP ends with CRLF
        if (!s.endsWith("\r\n")) s += "\r\n";
        return s;
      };
      
      const answerSdp = normalizeSdp(rawSdp);
      
      // Diagnostic: Log the ice-pwd line to detect concatenation issues
      const lines = answerSdp.split("\r\n");
      const icePwdLine = lines.find(l => l.includes("a=ice-pwd"));
      console.log("[SDP] ice-pwd line:", icePwdLine);
      console.log("[SDP] ice-pwd line length:", icePwdLine?.length);
      console.log("[SDP] ice-pwd contains multiple a=?", icePwdLine ? (icePwdLine.match(/a=/g) || []).length : 0);
      console.log("[SDP] Total lines after normalization:", lines.length);
      
      if (!answerSdp.startsWith('v=')) {
        console.error('[RealtimeVoice] Invalid SDP answer format! Got:', answerSdp.substring(0, 100));
        throw new Error(`Invalid SDP answer format. Expected 'v=...' but got: ${answerSdp.substring(0, 50)}`);
      }
      
      // Step 4: Set remote description (answer)
      const answer = {
        type: 'answer',
        sdp: answerSdp
      };
      
      console.log('[RealtimeVoice] Setting remote description...');
      await peerConnectionRef.current.setRemoteDescription(answer);
      
      console.log('[RealtimeVoice] ✅ WebRTC connection established!');
      
      // Generate a session ID for tracking
      const sessionId = `rtc_${Date.now()}`;
      setSessionId(sessionId);
      setKeySource('organization'); // Since we use org's API key
      
      // Start session timer
      startTimeRef.current = Date.now();
      sessionTimerRef.current = setInterval(() => {
        setSessionDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
      
      // Clear any previous errors on successful connection
      setError(null);
      updateConnectionState(ConnectionState.CONNECTED);
      
    } catch (err) {
      console.error('[RealtimeVoice] Connection failed:', err);
      setError(err.message);
      updateConnectionState(ConnectionState.ERROR);
      onError?.(err);
      
      // Cleanup on failure
      disconnect('connection_failed');
    }
  }, [
    connectionState, agentId, voice, welcomeMessage, businessName, threadId,
    activeOrganization, setupAudioElement, setupLocalAudio, setupDataChannel,
    updateConnectionState, onError
  ]);
  
  const disconnect = useCallback(async (reason = 'unknown') => {
    // Log stack trace to identify who called disconnect
    console.warn('[RealtimeVoice] disconnect() called. Reason:', reason);
    console.warn('[RealtimeVoice] Layers trace:', new Error().stack);
    
    // Clear error state on disconnect
    setError(null);
    
    // Stop session timer
    if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
    
    // Close data channel
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Stop local audio
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    // Remove audio element
    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current.remove();
      audioElementRef.current = null;
    }
    
    // Notify backend session ended
    if (sessionId) {
      try {
        const duration = startTimeRef.current ? (Date.now() - startTimeRef.current) / 1000 : 0;
        await fetchWithOrg(
          `${BACKEND_URL}/api/voice/realtime/session/${sessionId}/end`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ duration_seconds: duration })
          },
          activeOrganization?.organization_id
        );
      } catch (e) {
        console.warn('[RealtimeVoice] Failed to notify session end:', e);
      }
    }
    
    setSessionId(null);
    setSessionDuration(0);
    setIsAISpeaking(false);
    setIsUserSpeaking(false);
    updateConnectionState(ConnectionState.DISCONNECTED);
    
  }, [sessionId, activeOrganization, updateConnectionState]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MIC/OUTPUT CONTROLS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const toggleMic = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = isMicMuted;
        setIsMicMuted(!isMicMuted);
        console.log(`[RealtimeVoice] Microphone ${isMicMuted ? 'unmuted' : 'muted'}`);
      }
    }
  }, [isMicMuted]);
  
  const toggleOutput = useCallback(() => {
    if (audioElementRef.current) {
      audioElementRef.current.muted = !isOutputMuted;
      setIsOutputMuted(!isOutputMuted);
      console.log(`[RealtimeVoice] Output ${isOutputMuted ? 'unmuted' : 'muted'}`);
    }
  }, [isOutputMuted]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CLEANUP (only on component unmount)
  // ═══════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    return () => {
      console.log('[RealtimeVoice] Component unmounting, cleaning up...');
      // Direct cleanup without calling disconnect to avoid stale closure issues
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
      if (dataChannelRef.current) {
        dataChannelRef.current.close();
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioElementRef.current) {
        audioElementRef.current.srcObject = null;
        audioElementRef.current.remove();
      }
    };
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FORMAT HELPERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  
  return (
    <div className="flex flex-col items-center gap-4 p-4">
      {/* Connection Status Badge */}
      <div className="flex items-center gap-2">
        {connectionState === ConnectionState.CONNECTED && (
          <div className="flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
            <WifiHigh size={14} />
            <span>Connected • {formatDuration(sessionDuration)}</span>
            {keySource && (
              <span className="text-xs opacity-70">({keySource})</span>
            )}
          </div>
        )}
        {connectionState === ConnectionState.CONNECTING && (
          <div className="flex items-center gap-1 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
            <CircleNotch size={14} className="animate-spin" />
            <span>Connecting...</span>
          </div>
        )}
        {connectionState === ConnectionState.ERROR && (
          <div className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
            <WarningCircle size={14} />
            <span>Connection Error</span>
          </div>
        )}
        {connectionState === ConnectionState.DISCONNECTED && (
          <div className="flex items-center gap-1 px-3 py-1 bg-gray-500/20 text-gray-400 rounded-full text-sm">
            <WifiSlash size={14} />
            <span>Disconnected</span>
          </div>
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="w-full max-w-md p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}
      
      {/* Speaking Indicators */}
      {connectionState === ConnectionState.CONNECTED && (
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
            isUserSpeaking ? 'bg-blue-500/20 text-blue-400 scale-105' : 'bg-[#2D3748] text-[#718096]'
          }`}>
            <Microphone size={16} />
            <span className="text-sm">You</span>
          </div>
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
            isAISpeaking ? 'bg-purple-500/20 text-purple-400 scale-105' : 'bg-[#2D3748] text-[#718096]'
          }`}>
            <Lightning size={16} />
            <span className="text-sm">AI</span>
          </div>
        </div>
      )}
      
      {/* Control Buttons */}
      <div className="flex items-center gap-3">
        {connectionState === ConnectionState.DISCONNECTED || connectionState === ConnectionState.ERROR ? (
          <button
            onClick={connect}
            className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition-colors"
          >
            <Phone size={20} />
            Start Realtime Call
          </button>
        ) : connectionState === ConnectionState.CONNECTING ? (
          <button
            disabled
            className="flex items-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-full font-semibold opacity-50 cursor-not-allowed"
          >
            <CircleNotch size={20} className="animate-spin" />
            Connecting...
          </button>
        ) : (
          <>
            {/* Microphone Toggle */}
            <button
              onClick={toggleMic}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                isMicMuted
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  : 'bg-[#2D3748] text-white hover:bg-[#3D4758]'
              }`}
              title={isMicMuted ? 'Unmute Microphone' : 'Mute Microphone'}
            >
              {isMicMuted ? <MicrophoneSlash size={20} /> : <Microphone size={20} />}
            </button>
            
            {/* Output Toggle */}
            <button
              onClick={toggleOutput}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                isOutputMuted
                  ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                  : 'bg-[#2D3748] text-white hover:bg-[#3D4758]'
              }`}
              title={isOutputMuted ? 'Unmute Output' : 'Mute Output'}
            >
              {isOutputMuted ? <SpeakerX size={20} /> : <SpeakerHigh size={20} />}
            </button>
            
            {/* Disconnect */}
            <button
              onClick={() => disconnect('user_clicked_end')}
              className="w-12 h-12 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
              title="End Call"
            >
              <PhoneDisconnect size={20} />
            </button>
          </>
        )}
      </div>
      
      {/* Barge-in hint */}
      {connectionState === ConnectionState.CONNECTED && (
        <p className="text-xs text-[#718096] text-center max-w-xs">
          💡 Speak anytime to interrupt the AI (barge-in supported)
        </p>
      )}
    </div>
  );
};

export default RealtimeVoiceSession;
